<template>
  <div>
    <!-- Page Header -->
    <CRow class="mb-3">
      <CCol>
        <h2>Tax Periods</h2>
        <p class="text-muted">Manage monthly tax periods and filings</p>
      </CCol>
      <CCol class="text-right">
        <CButton color="success" @click="showCreateModal = true">
          <CIcon name="cil-plus" /> Create Period
        </CButton>
        <CButton color="primary" class="ml-2" @click="$router.push('/tenants/tax/dashboard')">
          <CIcon name="cil-chart-line" /> Dashboard
        </CButton>
      </CCol>
    </CRow>

    <!-- Filters -->
    <CCard>
      <CCardHeader>
        <CIcon name="cil-filter" /> Filters
      </CCardHeader>
      <CCardBody>
        <CForm @submit.prevent="applyFilters">
          <CRow>
            <CCol md="3">
              <CInput
                label="Year"
                type="number"
                v-model="filters.year"
                placeholder="e.g., 2026"
                min="2020"
                :max="new Date().getFullYear() + 1"
              />
            </CCol>
            <CCol md="3">
              <CSelect
                label="Status"
                :options="statusOptions"
                v-model="filters.status"
                placeholder="All Statuses"
              />
            </CCol>
            <CCol md="3">
              <CInput
                label="Start Date"
                type="date"
                v-model="filters.startDate"
              />
            </CCol>
            <CCol md="3">
              <CInput
                label="End Date"
                type="date"
                v-model="filters.endDate"
              />
            </CCol>
          </CRow>
          <CRow class="mt-3">
            <CCol>
              <CButton type="submit" color="primary" size="sm">
                <CIcon name="cil-filter" /> Apply Filters
              </CButton>
              <CButton
                color="secondary"
                size="sm"
                class="ml-2"
                @click="clearFilters"
              >
                Clear
              </CButton>
            </CCol>
          </CRow>
        </CForm>
      </CCardBody>
    </CCard>

    <!-- Tax Periods Table -->
    <CCard>
      <CCardHeader>
        <strong>Tax Periods</strong>
        <span class="ml-2 text-muted" v-if="totalItems">({{ totalItems}} periods)</span>
      </CCardHeader>
      <CCardBody>
        <!-- Loading State -->
        <div v-if="loading" class="text-center py-5">
          <CSpinner color="primary" />
          <p class="mt-3">Loading tax periods...</p>
        </div>

        <!-- Error State -->
        <CAlert v-else-if="error" color="danger">
          <h4>Error Loading Periods</h4>
          <p>{{ error }}</p>
          <CButton color="danger" variant="outline" size="sm" @click="loadPeriods">
            Try Again
          </CButton>
        </CAlert>

        <!-- Empty State -->
        <CAlert v-else-if="periods.length === 0" color="info">
          <h4>No Tax Periods Found</h4>
          <p>No tax periods match your filters. Try adjusting your search criteria or create a new period.</p>
          <CButton color="success" @click="showCreateModal = true">
            <CIcon name="cil-plus" /> Create First Period
          </CButton>
        </CAlert>

        <!-- Table -->
        <template v-else>
          <CDataTable
            :items="periods"
            :fields="fields"
            hover
            striped
            clickable-rows
            @row-clicked="viewPeriodDetails"
            sorter
            :items-per-page-select="{ label: 'Periods per page:', values: [10, 20, 50, 100] }"
          >
            <template #periodCode="{item}">
              <td>
                <strong>{{ item.periodCode }}</strong>
                <br>
                <small class="text-muted">
                  {{ formatDate(item.startDate) }} - {{ formatDate(item.endDate) }}
                </small>
              </td>
            </template>

            <template #totalOutputTax="{item}">
              <td class="text-right text-danger">
                {{ formatCurrency(item.totalOutputTax) }}
                <br>
                <small class="text-muted">{{ item.invoiceCount || 0 }} invoices</small>
              </td>
            </template>

            <template #totalInputTax="{item}">
              <td v-if="deductsInputTax(item)" class="text-right text-success">
                {{ formatCurrency(item.totalInputTax) }}
                <br>
                <small class="text-muted">{{ item.billCount || 0 }} bills</small>
              </td>
              <td v-else class="text-right text-muted">
                Not claimable
                <CIcon
                  name="cil-info"
                  size="sm"
                  class="ml-1"
                  tabindex="0"
                  :aria-label="notClaimableHint"
                  v-c-tooltip="{ content: notClaimableHint, placement: 'top' }"
                />
                <br>
                <small class="text-muted">{{ formatCurrency(item.inputTaxPaid || 0) }} SST paid · {{ item.billCount || 0 }} bills</small>
              </td>
            </template>

            <template #netTaxPayable="{item}">
              <td class="text-right">
                <strong :class="item.netTaxPayable < 0 ? 'text-success' : 'text-primary'">
                  {{ formatCurrency(Math.abs(item.netTaxPayable)) }}
                </strong>
                <br>
                <small v-if="item.netTaxPayable < 0" class="text-success">Refundable</small>
                <small v-else class="text-muted">{{ item.transactionCount || 0 }} transactions</small>
              </td>
            </template>

            <template #dueDate="{item}">
              <td>
                {{ formatDate(item.dueDate) }}
                <br>
                <CBadge :color="getUrgencyColor(item)">
                  {{ getUrgencyText(item) }}
                </CBadge>
              </td>
            </template>

            <template #status="{item}">
              <td>
                <CBadge :color="getStatusColor(item.status)">
                  {{ getStatusLabel(item.status) }}
                </CBadge>
              </td>
            </template>

            <template #actions="{item}">
              <td class="text-right">
                <CDropdown
                  color="primary"
                  size="sm"
                  :toggler-text="'Actions'"
                  placement="bottom-end"
                >
                  <CDropdownItem @click.stop="viewPeriodDetails(item)">
                    <CIcon name="cil-file" /> View Details
                  </CDropdownItem>
                  <CDropdownItem @click.stop="downloadSST03(item.id)">
                    <CIcon name="cil-cloud-download" /> Download SST-03
                  </CDropdownItem>
                  <CDropdownDivider />
                  <CDropdownItem @click.stop="recalculatePeriod(item)">
                    <CIcon name="cil-reload" /> Recalculate
                  </CDropdownItem>
                  <CDropdownItem
                    v-if="item.status === 0"
                    @click.stop="closePeriod(item)"
                  >
                    <CIcon name="cil-lock-locked" /> Close Period
                  </CDropdownItem>
                  <CDropdownItem
                    v-if="item.status === 1"
                    @click.stop="filePeriod(item)"
                  >
                    <CIcon name="cil-check" /> Mark as Filed
                  </CDropdownItem>
                  <CDropdownItem
                    v-if="item.status !== 0"
                    @click.stop="reopenPeriod(item)"
                  >
                    <CIcon name="cil-lock-unlocked" /> Reopen Period
                  </CDropdownItem>
                </CDropdown>
              </td>
            </template>
          </CDataTable>

          <!-- Pagination -->
          <CPagination
            v-if="totalPages > 1"
            :activePage.sync="currentPage"
            :pages="totalPages"
            @update:activePage="handlePageChange"
          />
        </template>
      </CCardBody>
    </CCard>

    <!-- Create Period Modal -->
    <CModal
      title="Create New Tax Period"
      color="success"
      :show.sync="showCreateModal"
      size="sm"
    >
      <CForm @submit.prevent="createPeriod">
        <CInput
          label="Year"
          type="number"
          v-model="newPeriod.year"
          :min="2020"
          :max="new Date().getFullYear() + 1"
          required
          placeholder="e.g., 2026"
        />
        <CSelect
          label="Month"
          :options="monthOptions"
          v-model="newPeriod.month"
          required
          placeholder="Select month"
        />
        <CAlert color="info" class="mt-3">
          <small>
            <strong>Note:</strong> Periods are usually created automatically when invoices/bills are approved.
            Only create manually if needed.
          </small>
        </CAlert>
      </CForm>

      <template #footer>
        <CButton color="secondary" @click="showCreateModal = false">
          Cancel
        </CButton>
        <CButton color="success" @click="createPeriod" :disabled="creating">
          <CSpinner v-if="creating" size="sm" class="mr-1" />
          Create Period
        </CButton>
      </template>
    </CModal>

    <!-- Confirm for period actions (UI-GUIDELINES 5.4); opened via askConfirm() -->
    <CModal :show.sync="confirmModal.show" :close-on-backdrop="false" size="sm" @update:show="v => !v && resolveConfirm(false)">
      <template #header>
        <h6 class="modal-title">{{ confirmModal.title }}</h6>
      </template>
      <p class="mb-0">{{ confirmModal.message }}</p>
      <template #footer>
        <CButton color="light" @click="resolveConfirm(false)">Cancel</CButton>
        <CButton :color="confirmModal.color" @click="resolveConfirm(true)">{{ confirmModal.confirmText }}</CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import TaxAPI from '@/lib/taxApi'
import { formatMyr } from '@/utils/money'

export default {
  name: 'TaxPeriodList',
  data() {
    return {
      loading: false,
      error: null,
      periods: [],
      currentPage: 1,
      pageSize: 20,
      totalItems: 0,
      totalPages: 0,
      filters: {
        year: new Date().getFullYear(),
        status: null,
        startDate: null,
        endDate: null
      },
      showCreateModal: false,
      creating: false,
      confirmModal: { show: false, title: '', message: '', confirmText: '', color: 'primary', resolve: null },
      notClaimableHint: "SST paid on purchases is part of your cost and can't be deducted. Ask your tax agent if your business qualifies, then change it in Business settings.",
      newPeriod: {
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1
      },
      fields: [
        { key: 'periodCode', label: 'Period', _style: 'min-width:150px' },
        { key: 'totalOutputTax', label: 'Output Tax', _style: 'min-width:120px' },
        { key: 'totalInputTax', label: 'Input tax', _style: 'min-width:120px' },
        { key: 'netTaxPayable', label: 'Net Payable', _style: 'min-width:120px' },
        { key: 'dueDate', label: 'Due Date', _style: 'min-width:120px' },
        { key: 'status', label: 'Status', _style: 'width:100px' },
        { key: 'actions', label: '', _style: 'width:120px' }
      ],
      statusOptions: [
        { value: null, label: 'All Statuses' },
        { value: 0, label: 'Open' },
        { value: 1, label: 'Closed' },
        { value: 2, label: 'Filed' }
      ],
      monthOptions: [
        { value: 1, label: 'January' },
        { value: 2, label: 'February' },
        { value: 3, label: 'March' },
        { value: 4, label: 'April' },
        { value: 5, label: 'May' },
        { value: 6, label: 'June' },
        { value: 7, label: 'July' },
        { value: 8, label: 'August' },
        { value: 9, label: 'September' },
        { value: 10, label: 'October' },
        { value: 11, label: 'November' },
        { value: 12, label: 'December' }
      ]
    }
  },
  async mounted() {
    this.$store.commit("setPageNavItems", ["Tax Management", "Tax Periods"])
    await this.loadPeriods()
  },
  methods: {
    // Each period row carries inputTaxClaimable / inputTaxPaid. Show input tax as a deduction when
    // claimable (or unknown), or when the period's total still deducts it (not recalculated yet).
    deductsInputTax(period) {
      return period.inputTaxClaimable !== false || (parseFloat(period.totalInputTax) || 0) !== 0
    },

    async loadPeriods() {
      this.loading = true
      this.error = null

      try {
        const taxApi = new TaxAPI(window.localStorage.getItem('token'))

        const params = {
          page: this.currentPage,
          pageSize: this.pageSize
        }

        // Add filters if set
        if (this.filters.year) params.year = this.filters.year
        if (this.filters.status !== null) params.status = this.filters.status
        if (this.filters.startDate) params.startDate = this.filters.startDate
        if (this.filters.endDate) params.endDate = this.filters.endDate

        const response = await taxApi.getPeriods(params)
        const data = response.data.result

        // Backend PagedResult returns: data, totalRecords, totalPages (not items, totalCount)
        this.periods = data.data || []
        this.totalItems = data.totalRecords || 0
        this.totalPages = data.totalPages || 1
      } catch (error) {
        console.error('Error loading tax periods:', error)
        this.error = error.response?.data?.message || error.message || 'Failed to load periods'
      } finally {
        this.loading = false
      }
    },

    async applyFilters() {
      this.currentPage = 1
      await this.loadPeriods()
    },

    async clearFilters() {
      this.filters = {
        year: new Date().getFullYear(),
        status: null,
        startDate: null,
        endDate: null
      }
      this.currentPage = 1
      await this.loadPeriods()
    },

    async handlePageChange(page) {
      this.currentPage = page
      await this.loadPeriods()
    },

    viewPeriodDetails(period) {
      this.$router.push(`/tenants/tax/periods/${period.id}`)
    },

    async downloadSST03(periodId) {
      try {
        this.$router.push(`/tenants/tax/sst03/${periodId}`)
      } catch (error) {
        console.error('Error:', error)
        this.$toast.error("Couldn't open the SST-03 report. Check your connection and try again.")
      }
    },

    async createPeriod() {
      if (!this.newPeriod.year || !this.newPeriod.month) {
        this.$toast.error('Choose a year and month for the new period')
        return
      }

      this.creating = true

      try {
        const taxApi = new TaxAPI(window.localStorage.getItem('token'))
        await taxApi.createPeriod(this.newPeriod.year, this.newPeriod.month)

        this.$toast.success('Tax period created')
        this.showCreateModal = false
        this.newPeriod = {
          year: new Date().getFullYear(),
          month: new Date().getMonth() + 1
        }
        await this.loadPeriods()
      } catch (error) {
        console.error('Error creating period:', error)
        this.$toast.error("Couldn't create the period. " + (error.response?.data?.message || 'Check your connection and try again.'))
      } finally {
        this.creating = false
      }
    },

    // Resolves true when the user confirms, false on cancel/close
    askConfirm({ title, message, confirmText, color = 'primary' }) {
      return new Promise(resolve => {
        this.confirmModal = { show: true, title, message, confirmText, color, resolve }
      })
    },

    resolveConfirm(result) {
      const resolve = this.confirmModal.resolve
      this.confirmModal = { ...this.confirmModal, show: false, resolve: null }
      if (resolve) resolve(result)
    },

    async recalculatePeriod(period) {
      const ok = await this.askConfirm({
        title: `Recalculate period ${period.periodCode}?`,
        message: 'Output tax, input tax and the net payable are worked out again from the approved invoices and bills in this period.',
        confirmText: 'Recalculate'
      })
      if (!ok) return

      try {
        const taxApi = new TaxAPI(window.localStorage.getItem('token'))
        await taxApi.recalculatePeriod(period.id)

        this.$toast.success('Period totals recalculated')
        await this.loadPeriods()
      } catch (error) {
        console.error('Error recalculating:', error)
        this.$toast.error("Couldn't recalculate the period. Check your connection and try again.")
      }
    },

    async closePeriod(period) {
      const ok = await this.askConfirm({
        title: `Close period ${period.periodCode}?`,
        message: 'No more transactions can be added to this period. You can reopen it later if needed.',
        confirmText: 'Close period'
      })
      if (!ok) return

      try {
        const taxApi = new TaxAPI(window.localStorage.getItem('token'))
        await taxApi.updatePeriodStatus(period.id, 1) // 1 = Closed

        this.$toast.success('Period closed')
        await this.loadPeriods()
      } catch (error) {
        console.error('Error closing period:', error)
        this.$toast.error("Couldn't close the period. Check your connection and try again.")
      }
    },

    async filePeriod(period) {
      const ok = await this.askConfirm({
        title: `Mark period ${period.periodCode} as filed?`,
        message: 'Use this after you have submitted the SST-03 return to customs. Approved documents in a filed period can no longer be moved back to Draft.',
        confirmText: 'Mark as filed'
      })
      if (!ok) return

      try {
        const taxApi = new TaxAPI(window.localStorage.getItem('token'))
        await taxApi.updatePeriodStatus(period.id, 2) // 2 = Filed

        this.$toast.success('Period marked as filed')
        await this.loadPeriods()
      } catch (error) {
        console.error('Error filing period:', error)
        this.$toast.error("Couldn't mark the period as filed. Check your connection and try again.")
      }
    },

    async reopenPeriod(period) {
      const ok = await this.askConfirm({
        title: `Reopen period ${period.periodCode}?`,
        message: 'Transactions can be added to this period again.',
        confirmText: 'Reopen period'
      })
      if (!ok) return

      try {
        const taxApi = new TaxAPI(window.localStorage.getItem('token'))
        await taxApi.updatePeriodStatus(period.id, 0) // 0 = Open

        this.$toast.success('Period reopened')
        await this.loadPeriods()
      } catch (error) {
        console.error('Error reopening period:', error)
        this.$toast.error("Couldn't reopen the period. Check your connection and try again.")
      }
    },

    formatCurrency(amount) {
      return `RM ${formatMyr(amount)}`
    },

    formatDate(date) {
      const taxApi = new TaxAPI()
      return taxApi.formatDate(date)
    },

    getStatusLabel(status) {
      const taxApi = new TaxAPI()
      return taxApi.getStatusLabel(status)
    },

    getStatusColor(status) {
      const taxApi = new TaxAPI()
      return taxApi.getStatusColor(status)
    },

    getUrgencyColor(period) {
      const taxApi = new TaxAPI()
      const badge = taxApi.getUrgencyBadge(period)
      return badge.color
    },

    getUrgencyText(period) {
      const taxApi = new TaxAPI()
      const badge = taxApi.getUrgencyBadge(period)
      return badge.text
    }
  }
}
</script>

<style scoped>
.text-right {
  text-align: right;
}
</style>